import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Search, Printer, Trash2, Eye, X, Calendar, Mail, Phone, 
  MapPin, Gift, Clock, Filter, RefreshCw, ChevronDown, Image, Download
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReferenceImage = ({ fileId, filename }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    const load = async () => {
      try {
        const token = localStorage.getItem("adminToken");
        const res = await axios.get(`${API}/files/treats/${fileId}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        });
        url = URL.createObjectURL(res.data);
        setSrc(url);
      } catch {}
    };
    load();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [fileId]);

  if (!src) {
    return (
      <div className="aspect-square bg-[#0F0F0F] flex flex-col items-center justify-center gap-1 border border-[rgba(212,175,55,0.1)]">
        <Image className="w-5 h-5 text-[#52525B]" />
        <span className="text-[#52525B] text-[10px] truncate max-w-full px-2">{filename}</span>
      </div>
    );
  }
  return <img src={src} alt={filename} className="aspect-square w-full object-cover border border-[rgba(212,175,55,0.1)]" />;
};

const STATUS_COLORS = {
  new: { bg: "bg-blue-500/10", text: "text-blue-400", border: "border-blue-500/30", label: "New" },
  contacted: { bg: "bg-yellow-500/10", text: "text-yellow-400", border: "border-yellow-500/30", label: "Contacted" },
  confirmed: { bg: "bg-green-500/10", text: "text-green-400", border: "border-green-500/30", label: "Confirmed" },
  completed: { bg: "bg-[#D4AF37]/10", text: "text-[#D4AF37]", border: "border-[#D4AF37]/30", label: "Completed" },
  cancelled: { bg: "bg-red-500/10", text: "text-red-400", border: "border-red-500/30", label: "Cancelled" },
};

const PrintTicket = ({ inquiry, onClose }) => {
  const printRef = useRef(null);
  const handlePrint = () => {
    const content = printRef.current;
    const win = window.open("", "_blank");
    win.document.write(`
      <html><head><title>Treats Inquiry #${inquiry.inquiry_code}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Georgia', serif; padding: 40px; color: #1a1a1a; }
        .ticket { max-width: 600px; margin: 0 auto; border: 2px solid #D4AF37; padding: 40px; }
        .header { text-align: center; border-bottom: 1px solid #D4AF37; padding-bottom: 20px; margin-bottom: 24px; }
        .brand { font-size: 22px; font-weight: bold; letter-spacing: 2px; color: #1a1a1a; }
        .brand-accent { color: #D4AF37; }
        .code { font-size: 14px; color: #666; margin-top: 6px; letter-spacing: 1px; }
        .section { margin-bottom: 20px; }
        .section-title { font-size: 11px; text-transform: uppercase; letter-spacing: 2px; color: #D4AF37; margin-bottom: 8px; font-weight: bold; }
        .field { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #eee; font-size: 14px; }
        .field-label { color: #666; }
        .field-value { font-weight: 600; text-align: right; max-width: 60%; }
        .notes { font-size: 13px; line-height: 1.6; color: #444; padding: 12px; background: #f9f9f9; margin-top: 8px; }
        .footer { text-align: center; margin-top: 24px; padding-top: 20px; border-top: 1px solid #D4AF37; font-size: 11px; color: #999; }
        @media print { body { padding: 20px; } .ticket { border: 1px solid #D4AF37; } }
      </style></head><body>
      <div class="ticket">
        <div class="header">
          <div class="brand">MIX & MINGLE <span class="brand-accent">Treats</span></div>
          <div class="code">INQUIRY ${inquiry.inquiry_code}</div>
        </div>
        <div class="section">
          <div class="section-title">Client Information</div>
          <div class="field"><span class="field-label">Name</span><span class="field-value">${inquiry.name}</span></div>
          <div class="field"><span class="field-label">Email</span><span class="field-value">${inquiry.email}</span></div>
          ${inquiry.phone ? `<div class="field"><span class="field-label">Phone</span><span class="field-value">${inquiry.phone}</span></div>` : ""}
        </div>
        <div class="section">
          <div class="section-title">Event Details</div>
          <div class="field"><span class="field-label">Event Type</span><span class="field-value">${inquiry.event_type}</span></div>
          ${inquiry.selected_package ? `<div class="field"><span class="field-label">Package</span><span class="field-value">${inquiry.selected_package}</span></div>` : ""}
          ${inquiry.event_date ? `<div class="field"><span class="field-label">Event Date</span><span class="field-value">${new Date(inquiry.event_date).toLocaleDateString("en-CA", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</span></div>` : ""}
          ${inquiry.quantity ? `<div class="field"><span class="field-label">Quantity</span><span class="field-value">${inquiry.quantity}</span></div>` : ""}
          ${inquiry.delivery_preference ? `<div class="field"><span class="field-label">Fulfillment</span><span class="field-value">${inquiry.delivery_preference}</span></div>` : ""}
        </div>
        ${inquiry.requested_treats ? `
        <div class="section">
          <div class="section-title">Requested Treats</div>
          <div class="notes">${inquiry.requested_treats}</div>
        </div>` : ""}
        ${inquiry.inspiration ? `
        <div class="section">
          <div class="section-title">Inspiration / Details</div>
          <div class="notes">${inquiry.inspiration}</div>
        </div>` : ""}
        <div class="section">
          <div class="section-title">Order Status</div>
          <div class="field"><span class="field-label">Status</span><span class="field-value">${(inquiry.status || "new").toUpperCase()}</span></div>
          <div class="field"><span class="field-label">Submitted</span><span class="field-value">${new Date(inquiry.created_at).toLocaleString("en-CA")}</span></div>
        </div>
        <div class="footer">MIX & MINGLE Events &bull; mixandmingleevents.com</div>
      </div>
      </body></html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 500);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-[#121212] border border-[rgba(212,175,55,0.3)] max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-[rgba(212,175,55,0.15)]">
          <h3 className="font-['Playfair_Display'] text-lg text-[#F5F5F0]">
            Inquiry Ticket &mdash; {inquiry.inquiry_code}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#050505] text-sm font-semibold hover:bg-[#F7E7CE] transition-colors"
              data-testid="print-ticket-btn"
            >
              <Printer className="w-4 h-4" /> Print
            </button>
            <button onClick={onClose} className="p-2 text-[#A1A1AA] hover:text-[#F5F5F0]">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div ref={printRef} className="p-6 space-y-6">
          <div>
            <p className="text-[#D4AF37] text-xs uppercase tracking-widest mb-3">Client Information</p>
            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-[#52525B] text-xs uppercase tracking-wider">Name</p><p className="text-[#F5F5F0]">{inquiry.name}</p></div>
              <div><p className="text-[#52525B] text-xs uppercase tracking-wider">Email</p><p className="text-[#F5F5F0]">{inquiry.email}</p></div>
              {inquiry.phone && <div><p className="text-[#52525B] text-xs uppercase tracking-wider">Phone</p><p className="text-[#F5F5F0]">{inquiry.phone}</p></div>}
              {inquiry.delivery_preference && <div><p className="text-[#52525B] text-xs uppercase tracking-wider">Fulfillment</p><p className="text-[#F5F5F0]">{inquiry.delivery_preference}</p></div>}
            </div>
          </div>
          <div className="border-t border-[rgba(212,175,55,0.15)] pt-4">
            <p className="text-[#D4AF37] text-xs uppercase tracking-widest mb-3">Event Details</p>
            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-[#52525B] text-xs uppercase tracking-wider">Event Type</p><p className="text-[#F5F5F0]">{inquiry.event_type}</p></div>
              {inquiry.selected_package && <div><p className="text-[#52525B] text-xs uppercase tracking-wider">Package</p><p className="text-[#D4AF37] font-semibold">{inquiry.selected_package}</p></div>}
              {inquiry.event_date && <div><p className="text-[#52525B] text-xs uppercase tracking-wider">Event Date</p><p className="text-[#F5F5F0]">{new Date(inquiry.event_date).toLocaleDateString("en-CA", { weekday: "short", year: "numeric", month: "short", day: "numeric" })}</p></div>}
              {inquiry.quantity && <div><p className="text-[#52525B] text-xs uppercase tracking-wider">Quantity</p><p className="text-[#F5F5F0]">{inquiry.quantity}</p></div>}
            </div>
          </div>
          {inquiry.requested_treats && (
            <div className="border-t border-[rgba(212,175,55,0.15)] pt-4">
              <p className="text-[#52525B] text-xs uppercase tracking-wider mb-1">Requested Treats</p>
              <p className="text-[#F5F5F0]">{inquiry.requested_treats}</p>
            </div>
          )}
          {inquiry.inspiration && (
            <div className="border-t border-[rgba(212,175,55,0.15)] pt-4">
              <p className="text-[#52525B] text-xs uppercase tracking-wider mb-1">Inspiration / Details</p>
              <p className="text-[#A1A1AA] leading-relaxed">{inquiry.inspiration}</p>
            </div>
          )}
          {inquiry.reference_files && inquiry.reference_files.length > 0 && (
            <div className="border-t border-[rgba(212,175,55,0.15)] pt-4">
              <p className="text-[#D4AF37] text-xs uppercase tracking-widest mb-3">Reference Images</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {inquiry.reference_files.map((file, idx) => (
                  <div key={file.id || idx} className="group relative">
                    <ReferenceImage fileId={file.id} filename={file.filename} />
                    <a
                      href={`${API}/files/treats/${file.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute bottom-2 right-2 p-1.5 bg-[#050505]/80 text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Open full size"
                    >
                      <Download className="w-3 h-3" />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

const AdminTreatsInquiries = () => {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/admin/treats-inquiries`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInquiries(res.data);
    } catch (err) {
      toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInquiries(); }, []);

  const updateStatus = async (id, status) => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.put(`${API}/admin/treats-inquiries/${id}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInquiries(prev => prev.map(i => i.id === id ? { ...i, status } : i));
      toast.success(`Status updated to ${status}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const deleteInquiry = async (id) => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(`${API}/admin/treats-inquiries/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInquiries(prev => prev.filter(i => i.id !== id));
      setDeleteId(null);
      toast.success("Inquiry deleted");
    } catch {
      toast.error("Failed to delete inquiry");
    }
  };

  const filtered = inquiries.filter(i => {
    const matchSearch = !searchTerm ||
      i.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      i.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      i.inquiry_code?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === "all" || (i.status || "new") === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-treats-page">
      {/* Header */}
      <div className="border-b border-[rgba(212,175,55,0.15)] bg-[#0F0F0F]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/admin/dashboard")}
                className="p-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="font-['Playfair_Display'] text-xl text-[#F5F5F0]">Treats Inquiries</h1>
                <p className="text-[#52525B] text-sm">{inquiries.length} total inquiries</p>
              </div>
            </div>
            <button
              onClick={fetchInquiries}
              className="flex items-center gap-2 px-3 py-2 text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors border border-[rgba(212,175,55,0.15)] hover:border-[rgba(212,175,55,0.4)]"
              data-testid="refresh-inquiries"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
            <input
              type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, or code..."
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.15)] text-[#F5F5F0] pl-10 pr-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none"
              data-testid="inquiry-search"
            />
          </div>
          <div className="flex gap-2">
            {["all", "new", "contacted", "confirmed", "completed", "cancelled"].map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-2 text-xs uppercase tracking-wider font-semibold transition-all border ${
                  statusFilter === s
                    ? "bg-[#D4AF37] text-[#050505] border-[#D4AF37]"
                    : "text-[#A1A1AA] border-[rgba(212,175,55,0.15)] hover:border-[rgba(212,175,55,0.4)]"
                }`}
                data-testid={`filter-${s}`}
              >
                {s === "all" ? "All" : s}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-[#52525B]" data-testid="no-inquiries">
            <Gift className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p className="text-lg">No inquiries found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="inquiries-table">
              <thead>
                <tr className="border-b border-[rgba(212,175,55,0.15)]">
                  {["Code", "Client", "Event Type", "Package", "Date", "Treats", "Files", "Status", "Submitted", "Actions"].map(h => (
                    <th key={h} className="text-left text-[#52525B] text-xs uppercase tracking-wider py-3 px-4 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((inq) => {
                  const st = STATUS_COLORS[inq.status || "new"];
                  return (
                    <tr key={inq.id} className="border-b border-[rgba(212,175,55,0.08)] hover:bg-[#0F0F0F] transition-colors" data-testid={`inquiry-row-${inq.id}`}>
                      <td className="py-4 px-4">
                        <span className="text-[#D4AF37] text-sm font-mono">{inq.inquiry_code}</span>
                      </td>
                      <td className="py-4 px-4">
                        <p className="text-[#F5F5F0] text-sm font-semibold">{inq.name}</p>
                        <p className="text-[#52525B] text-xs">{inq.email}</p>
                      </td>
                      <td className="py-4 px-4 text-[#A1A1AA] text-sm">{inq.event_type}</td>
                      <td className="py-4 px-4 text-[#D4AF37] text-sm">{inq.selected_package || "--"}</td>
                      <td className="py-4 px-4 text-[#A1A1AA] text-sm">
                        {inq.event_date ? new Date(inq.event_date).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "--"}
                      </td>
                      <td className="py-4 px-4 text-[#A1A1AA] text-sm max-w-[200px] truncate">{inq.requested_treats || "--"}</td>
                      <td className="py-4 px-4">
                        {inq.reference_files && inq.reference_files.length > 0 ? (
                          <span className="flex items-center gap-1 text-[#D4AF37] text-xs">
                            <Image className="w-3 h-3" /> {inq.reference_files.length}
                          </span>
                        ) : (
                          <span className="text-[#52525B] text-xs">--</span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <select
                          value={inq.status || "new"}
                          onChange={(e) => updateStatus(inq.id, e.target.value)}
                          className={`text-xs px-2 py-1 border ${st.border} ${st.bg} ${st.text} bg-transparent focus:outline-none cursor-pointer`}
                          data-testid={`status-select-${inq.id}`}
                        >
                          {Object.entries(STATUS_COLORS).map(([k, v]) => (
                            <option key={k} value={k} className="bg-[#121212] text-[#F5F5F0]">{v.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="py-4 px-4 text-[#52525B] text-xs">
                        {new Date(inq.created_at).toLocaleDateString("en-CA")}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setSelectedInquiry(inq)}
                            className="p-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
                            title="View & Print"
                            data-testid={`view-inquiry-${inq.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteId(inq.id)}
                            className="p-2 text-[#A1A1AA] hover:text-red-400 transition-colors"
                            title="Delete"
                            data-testid={`delete-inquiry-${inq.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Print Ticket Modal */}
      <AnimatePresence>
        {selectedInquiry && (
          <PrintTicket inquiry={selectedInquiry} onClose={() => setSelectedInquiry(null)} />
        )}
      </AnimatePresence>

      {/* Delete Confirmation */}
      <AnimatePresence>
        {deleteId && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setDeleteId(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[#121212] border border-[rgba(212,175,55,0.3)] p-6 max-w-sm w-full"
              onClick={(e) => e.stopPropagation()}
              data-testid="delete-confirm-modal"
            >
              <h3 className="font-['Playfair_Display'] text-lg text-[#F5F5F0] mb-3">Delete Inquiry?</h3>
              <p className="text-[#A1A1AA] text-sm mb-6">This action cannot be undone.</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteId(null)} className="flex-1 py-2 border border-[rgba(212,175,55,0.3)] text-[#A1A1AA] text-sm hover:text-[#F5F5F0]">
                  Cancel
                </button>
                <button
                  onClick={() => deleteInquiry(deleteId)}
                  className="flex-1 py-2 bg-red-500/20 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/30"
                  data-testid="confirm-delete-btn"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminTreatsInquiries;
